import { alpha, createTheme } from '@mui/material'
import { MuiLinearProgress } from './components-overrides'
import { resizableStyles } from './resizable-styles'

export const mainFont = 'Fira Sans'
export const secondaryFont = 'Space Grotesk'
export const buttonSize = {
    small: '32px',
    medium: '36px',
    large: '48px',
}

export const basicTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 640,
            md: 1024,
            lg: 1440,
            xl: 2048,
        },
    },
    palette: {
        mode: 'dark',
        common: {
            black: '#101820',
            white: '#FFFFFF',
        },
        primary: {
            light: '#C8F2F9',
            main: '#A5E9F6',
            dark: '#7BAFB8',
            contrastText: '#101820',
        },
        secondary: {
            light: '#FFEFC5',
            main: '#FFE49E',
            dark: '#CCB67E',
            contrastText: '#101820',
        },
        error: {
            light: '#FF7D7D',
            main: '#FF5C5C',
            dark: '#CF4343',
            contrastText: '#101820',
        },
        success: {
            light: '#B8F68E',
            main: '#82C057',
            dark: '#4F7334',
            contrastText: '#101820',
        },
        warning: {
            light: '#F98080',
            main: '#C05757',
            dark: '#8B3D3D',
            contrastText: '#FFFFFF',
        },
        info: {
            light: '#FFD699',
            main: '#FFC58C',
            dark: '#CC9A6F',
            contrastText: '#101820',
        },
        background: {
            default: '#101820',
            paper: '#131D26',
        },
        text: {
            primary: '#FFFFFF',
            secondary: '#A5E9F6',
            disabled: '#8F8F8F',
        },
        orange: {
            light: '#FFD699',
            main: '#FFC58C',
            dark: '#CC9A6F',
            contrastText: '#101820',
        },
        glow: {
            light: '#E6FF66',
            main: '#DCFB19',
            dark: '#A6B800',
            contrastText: '#101820',
        },
    },
})

let theme = basicTheme

theme = createTheme(theme, {
    typography: {
        fontFamily: [secondaryFont, 'Sans Serif'].join(','),
        h1: {
            fontSize: '60px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '60px',
        },
        h2: {
            fontSize: '40px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '42px',
        },
        h3: {
            fontSize: '36px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '38px',
        },
        h4: {
            fontSize: '32px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '34px',
        },
        h5: {
            fontSize: '24px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '26px',
        },
        h6: {
            fontSize: '18px',
            fontFamily: mainFont,
            fontWeight: 'bold',
            lineHeight: '24px',
        },
        body1: {
            fontSize: '18px',
            fontFamily: secondaryFont,
            fontWeight: 'regular',
            lineHeight: '20px',
        },
        body2: {
            fontSize: '16px',
            fontFamily: secondaryFont,
            fontWeight: 'regular',
            lineHeight: '18px',
        },
        body3: {
            fontSize: '24px',
            fontFamily: secondaryFont,
            fontWeight: 700,
            lineHeight: '34px',
        },
        body4: {
            fontSize: '18px',
            fontFamily: secondaryFont,
            fontWeight: 700,
            lineHeight: '25px',
        },
        button: {
            fontFamily: secondaryFont,
            fontWeight: 700,
            lineHeight: 'normal',
            textTransform: 'uppercase',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                ...resizableStyles,
                '*': {
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.primary.main} transparent`,
                },
                '#root:has(#missions-UI)': {
                    '@media(min-width: 1024px)': {
                        '::after': {
                            content: '""',
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            width: '100%',
                            height: '100vh',
                            background: 'url(/assets/backgrounds/missions.svg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            zIndex: -1,
                            // filter: 'blur(6px)',
                        },
                    },
                },
                body: {
                    overflowX: 'hidden',
                    userSelect: 'none',
                    '& .MuiDialog-root+.MuiDialog-root': {
                        display: 'none',
                    },
                },
                img: {
                    pointerEvents: 'none',
                    userSelect: 'none',
                },
                a: {
                    cursor: 'pointer',
                    color: 'inherit',
                    textDecoration: 'under',
                },
                html: {
                    fontSize: '16px',
                },

                'div[title="Choose promotion piece"]': {
                    backgroundColor: 'transparent !important',
                },

                'input[type=number]': {
                    MozAppearance: 'textfield',
                },
                'input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
                'input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                size: 'medium',
                variant: 'outlined',
                color: 'primary',
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    boxSizing: 'border-box',
                    minWidth: '160px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    // '&.MuiButton-sizeLarge': {
                    //     border: `4px solid transparent`,
                    // },
                    ':hover:not(:active)': {
                        // backgroundColor: theme.palette.common.white,
                    },

                    ':active': {
                        borderColor: `${theme.palette.common.white}`,
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'outlined' },
                    style: (props) => ({
                        backgroundColor: theme.palette.background.paper,
                        borderWidth: '1px !important',
                        borderColor: theme.palette.common.white + ' !important',
                        color: theme.palette.common.white,
                        ':hover:not(:active)': {
                            color: theme.palette[props.ownerState.color].main,
                        },
                        ':active': {
                            backgroundColor: theme.palette[props.ownerState.color].dark,
                            color: theme.palette.common.black,
                        },
                    }),
                },
                {
                    props: { size: 'large' },
                    style: {
                        height: buttonSize.large,
                        fontSize: `calc(${buttonSize.large} / 2)`,
                        svg: {
                            width: `calc(${buttonSize.large} / 1.5)`,
                            height: `calc(${buttonSize.large} / 1.5)`,
                        },
                    },
                },
                {
                    props: { size: 'medium' },
                    style: {
                        height: buttonSize.medium,
                        fontSize: `calc(${buttonSize.medium} / 2)`,
                        svg: {
                            width: `calc(${buttonSize.medium} / 1.5)`,
                            height: `calc(${buttonSize.medium} / 1.5)`,
                        },
                    },
                },
                {
                    props: { size: 'small' },
                    style: {
                        padding: '4px 2px',
                        height: buttonSize.small,
                        fontSize: `calc(${buttonSize.small} / 2)`,
                        svg: {
                            width: `calc(${buttonSize.small} / 1.5)`,
                            height: `calc(${buttonSize.small} / 1.5)`,
                        },
                    },
                },
                {
                    props: { fullWidth: true },
                    style: {
                        minWidth: 'unset',
                    },
                },
                {
                    props: { onlyIcon: true },
                    style: {
                        '> span': {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: '0',
                        },
                        minWidth: 'unset',
                        width: 'fit-content',
                        aspectRatio: '1/1',
                        padding: '0 !important',
                        svg: {
                            width: '85%',
                            height: '85%',
                        },
                    },
                },
                {
                    props: { selected: true },
                    style: (props) => ({
                        backgroundColor: theme.palette[props.ownerState.color].main + `!important`,
                        borderColor: `transparent !important`,
                        color: theme.palette.common.black + ' !important',
                        cursor: 'default',
                    }),
                },
                {
                    props: { tab: true },
                    style: {
                        minWidth: 'unset',
                        // borderRadius: '6px 6px 0 0',
                        // borderColor: theme.palette.common.white + ' !important',
                        // borderBottom: 'none',
                    },
                },
                {
                    props: { badge: true },
                    style: {
                        position: 'relative',
                        '&:after': {
                            content: '""',
                            textAlign: 'center',
                            position: 'absolute',
                            top: '-2px', // border-size is 2px
                            right: '-2px', // border-size is 2px
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: `#FF5C5C`,
                            filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.5))',
                            transform: 'translate(50%, -50%)',
                        },
                    },
                },
                {
                    props: { animated: true },
                    style: (props) => ({
                        '@keyframes icon-jiggle': {
                            '0%': { transform: 'rotate(0deg) scale(1)' },
                            '20%': { transform: 'rotate(0deg) scale(1.5)' },
                            '30%': { transform: 'rotate(-10deg) scale(1.5)' },
                            '40%': { transform: 'rotate(10deg) scale(1.50)' },
                            '50%': { transform: 'rotate(-10deg) scale(1.50)' },
                            '60%': { transform: 'rotate(10deg) scale(1.50)' },
                            '70%': { transform: 'rotate(-10deg) scale(1.50)' },
                            '80%': { transform: 'rotate(10deg) scale(1.50)' },
                            '100%': { transform: 'rotate(0deg) scale(1)' },
                        },
                        '@keyframes button-glow': {
                            '0%': {
                                boxShadow: `0 0 5px ${alpha(theme.palette.background.default, 0)})`,
                                filter: `drop-shadow(0 0 2px  ${alpha(theme.palette.background.default, 0)})`,
                            },
                            '50%': {
                                boxShadow: `0 0 10px ${alpha(theme.palette[props.ownerState.color].main, 0.7)}`,
                                filter: `drop-shadow(0 0 5px ${alpha(theme.palette[props.ownerState.color].main, 0.7)})`,
                            },
                            '100%': {
                                boxShadow: `0 0 5px ${alpha(theme.palette.background.default, 0)})`,
                                filter: `drop-shadow(0 0 2px  ${alpha(theme.palette.background.default, 0)})`,
                            },
                        },

                        position: 'relative', // Good for positioning pseudo-elements if needed for glow
                        transition: 'transform 0.2s ease-out, box-shadow 0.3s ease-in-out', // Smooth transitions for hover/focus
                        animation: `button-glow 0.75s ease-in-out 2`, // Jiggle once, glow infinitely with a slight delay

                        '&:hover': {
                            // Optional: Enhance glow or add a slight scale on hover
                            boxShadow: `0 0 15px ${alpha(theme.palette[props.ownerState.color].main, 0.7)}`,
                        },
                        // Targeting the SVG icon specifically for the glow's drop-shadow
                        '& svg': {
                            // Or your SVG's class if not using MuiSvgIcon directly
                            animation: `icon-jiggle 1.5s ease-in-out 1`,
                        },
                    }),
                },
                {
                    props: { disabled: true },
                    style: {
                        backgroundColor: '#585D63 !important',
                        color: '#878B8F !important',
                        border: 'none !important',
                    },
                },
            ],
        },
        MuiLinearProgress,
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: '12px',
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                outlined: true,
                elevation: 0, // paper should have a default elevation of 0 so we can use it as a basis surface for many components
            },
            styleOverrides: {
                root: {
                    fill: theme.palette.text.primary,
                    backgroundImage: 'none',
                },
            },
            variants: [
                {
                    props: {
                        variant: 'outlined',
                    },
                    style: {
                        border: `2px solid ${theme.palette.common.white}`,
                        borderRadius: '12px',
                        padding: '12px',
                        boxShadow: 'none',
                        '.MuiButton-root': {
                            borderRadius: '6px',
                        },
                    },
                },
            ],
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {},
            },
        },
        MuiSlider: {
            styleOverrides: {
                valueLabel: {},
            },
        },

        MuiGrid: {
            defaultProps: {},
            styleOverrides: {
                root: {},
            },
        },

        MuiMobileStepper: {
            defaultProps: {
                variant: 'dots',
            },
            styleOverrides: {
                root: {
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                },
                dots: {
                    alignItems: 'center',
                    gap: '20px',
                },
                dot: {
                    border: '2px solid',
                    borderColor: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                    height: '12px',
                    width: '12px',
                    margin: 0,
                },
                dotActive: {
                    border: '2px solid',
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.common.white,
                    height: '18px !important',
                    width: '18px !important',
                },
            },
        },

        MuiDialogTitle: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    padding: '0 16px',
                    fontSize: '40px',
                    fontFamily: mainFont,
                    fontWeight: 'bold',
                    lineHeight: '42px',
                    textTransform: 'uppercase',
                    color: ownerState.color ?? theme.palette.secondary.light,
                }),
            },
        },

        MuiDialogContent: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    padding: '16px',
                    gap: ownerState.gap ?? '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexGrow: ownerState.verticalCentered ? 0 : 1,
                }),
            },
        },

        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(theme.palette.background.default, 0.85),
                },
            },
        },

        MuiDialog: {
            defaultProps: {
                fullScreen: true,
                maxWidth: 'md',
                fullWidth: false,
                borderColor: theme.palette.common.white,
                autoHeight: false,
            },
            styleOverrides: {
                root: ({ ownerState }) => ({
                    zIndex: '1600',
                    '& .MuiDialog-paper': {
                        margin: 0,
                        padding: '24px',
                        maxWidth: ownerState.fullScreen ? 'auto' : '340px',
                        width: ownerState.fullScreen ? 'auto' : '340px',
                        border: ownerState.fullScreen ? 'none' : `1px solid ${ownerState.borderColor} !important`,
                        borderRadius: ownerState.fullScreen ? 0 : '12px',
                        '@media (min-width:1024px)': {
                            border: `1px solid ${ownerState.borderColor} !important`,
                            borderRadius: '12px',
                            margin: 2,
                            padding: ownerState.padding ?? '30px 100px 30px 100px',
                            width: '680px',
                            maxWidth: '680px',
                            minHeight: ownerState.autoHeight ? 'auto' : 'min(90vh, 680px)',
                            height: 'auto',
                            maxHeight: '90vh',
                            '@media (max-height:760px)': {
                                height: '90vh',
                                minHeight: '90vh',
                                maxHeight: '90vh',
                            },
                        },
                    },
                }),
            },
            variants: [
                {
                    props: { horizontalCentered: true },
                    style: {
                        '& .MuiDialog-paper': {
                            alignItems: 'center',
                            textAlign: 'center',
                        },
                    },
                },
                {
                    props: { verticalCentered: true },
                    style: {
                        '& .MuiDialog-paper': {
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            justifyContent: 'center',
                        },
                    },
                },
                {
                    props: { forceBlock: true },
                    style: {
                        display: 'block !important',
                    },
                },
                {
                    props: { paddingLess: true },
                    style: {
                        '& .MuiDialog-paper': {
                            padding: '0px !important',
                            backgroundColor: 'transparent',
                            border: 'none !important',
                            boxShadow: 'none',
                        },
                    },
                },
            ],
        },

        MuiTextField: {
            defaultProps: {
                variant: 'standard',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                SelectProps: {
                    MenuProps: {
                        sx: { zIndex: 1600 },
                        PaperProps: {
                            sx: {
                                maxHeight: '30vh',
                            },
                        },
                    },
                },
            },
            styleOverrides: {
                root: {
                    marginTop: 0,
                    // accent border and label color when focused
                    '& .MuiInput-underline:after': {
                        borderBottomColor: theme.palette.primary.main,
                    },
                    '& .MuiInput-underline.Mui-error:after': {
                        borderBottomColor: theme.palette.primary.main,
                    },
                    '& .MuiInputLabel-root': {
                        color: theme.palette.text.primary,
                        fontWeight: 400,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.palette.primary.main,
                    },
                    // Prevent error state from changing label color
                    '& .MuiInputLabel-root.Mui-error': {
                        color: theme.palette.error.main,
                    },
                    '& .MuiInputLabel-root.Mui-focused.Mui-error': {
                        color: theme.palette.primary.main,
                    },
                    '& .MuiInputBase-input': {
                        color: theme.palette.text.primary,
                    },
                    '& .Mui-focused': {
                        svg: {
                            fill: theme.palette.primary.main,
                            path: { fill: theme.palette.primary.main },
                        },
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'outlined' },
                    style: {
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme.palette.text.primary,
                                borderRadius: '6px',
                            },
                            '&:hover fieldset': {
                                transition: 'border-color 250ms',
                                color: theme.palette.text.primary,
                                borderColor: theme.palette.text.primary,
                            },
                            '&.Mui-focused fieldset': {
                                transition: 'border-color 250ms',
                                color: theme.palette.text.primary,
                                borderColor: theme.palette.primary.main,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme.palette.text.primary,
                        },
                    },
                },
            ],
        },

        MuiLink: {
            styleOverrides: {
                root: {
                    color: theme.palette.text.primary,
                    textDecoration: 'underline',
                    fontWeight: 700,
                    '&:hover': {
                        color: theme.palette.primary.main,
                    },
                    '&:active': {
                        color: theme.palette.primary.dark,
                    },
                    '&.Mui-disabled': {
                        color: theme.palette.text.disabled,
                        pointerEvents: 'none',
                    },
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                arrow: false,
                PopperProps: {
                    sx: { zIndex: 1600 },
                },
            },
            styleOverrides: {
                tooltip: {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.secondary.main,
                    boxShadow: theme.shadows[1],
                    border: `1px solid`,
                    borderColor: 'secondary.main',
                    fontSize: '14px',
                    borderRadius: '8px',
                },
                arrow: {
                    fontSize: '1.5rem',
                    '&:before': {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.secondary.main}`,
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: '#252636',
                    borderWidth: '1px',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: theme.palette.text.primary,
                    '&:disabled': {
                        color: theme.palette.text.primary,
                        opacity: '0.5',
                    },
                },
                sizeSmall: {
                    height: '24px',
                    width: '24px',
                },
            },
        },
    },
})

export default theme
